import React, { useState, useEffect, useRef } from 'react';
import { 
  MessageCircle, 
  X, 
  Send, 
  Phone, 
  Calendar,
  ArrowLeft,
  User,
  Mail
} from 'lucide-react';

// Configuration
const WHATSAPP_NUMBER = '60175946661';
const PHONE_NUMBER = '+60175946661';
const COMPANY_NAME = 'MyHR Orbit';

const ChatbotWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentView, setCurrentView] = useState('menu'); // menu, livechat
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [chatStep, setChatStep] = useState('name'); // name, email, chat
  const [sessionId, setSessionId] = useState('');
  const messagesEndRef = useRef(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Generate session ID
  useEffect(() => {
    setSessionId(`chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  }, []);

  // Auto scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Send to backend
  const sendToBackend = async (message, type = 'message') => {
    try {
      await fetch(`${API_URL}/api/chatbot/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          name: userName,
          email: userEmail,
          message,
          type,
          timestamp: new Date().toISOString()
        })
      });
    } catch (error) {
      console.error('Chatbot error:', error);
    }
  };

  // Add bot message
  const addBotMessage = (text) => {
    setIsTyping(true);
    setTimeout(() => {
      setMessages(prev => [...prev, { type: 'bot', text, time: new Date() }]);
      setIsTyping(false);
    }, 600);
  };

  // Add user message
  const addUserMessage = (text) => {
    setMessages(prev => [...prev, { type: 'user', text, time: new Date() }]);
  };

  // Start live chat
  const startLiveChat = () => {
    setCurrentView('livechat');
    setChatStep('name');
    setMessages([]);
    setTimeout(() => {
      addBotMessage("Hi there! 👋 Welcome to MyHR Orbit support.");
      setTimeout(() => {
        addBotMessage("Before we connect you with our team, may I know your name?");
      }, 800);
    }, 300);
  };

  // Handle form submit
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!inputValue.trim()) return;

    const value = inputValue.trim();
    setInputValue('');
    addUserMessage(value);

    if (chatStep === 'name') {
      setUserName(value);
      sendToBackend(value, 'name');
      setTimeout(() => {
        addBotMessage(`Nice to meet you, ${value}! 😊`);
        setTimeout(() => {
          addBotMessage("Could you share your email? This helps us follow up properly.");
        }, 600);
        setChatStep('email');
      }, 500);
    } else if (chatStep === 'email') {
      setUserEmail(value);
      sendToBackend(value, 'email');
      setTimeout(() => {
        addBotMessage("Perfect! How can we help you today? Type your message below.");
        setChatStep('chat');
      }, 500);
    } else {
      sendToBackend(value, 'message');
      setTimeout(() => {
        addBotMessage("Thanks for your message! Our team will review it and get back to you shortly. You can also reach us via WhatsApp for faster response.");
      }, 500);
    }
  };

  // Open WhatsApp
  const openWhatsApp = () => {
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Hi! I'm interested in MyHR Orbit HR System.")}`;
    window.open(url, '_blank');
    sendToBackend('Clicked WhatsApp', 'action');
  };

  // Book meeting (you can link to Calendly or your booking system)
  const bookMeeting = () => {
    // For now, open WhatsApp with meeting request
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Hi! I'd like to schedule a demo/meeting for MyHR Orbit.")}`;
    window.open(url, '_blank');
    sendToBackend('Clicked Book Meeting', 'action');
  };

  // Call
  const makeCall = () => {
    window.open(`tel:${PHONE_NUMBER}`, '_self');
    sendToBackend('Clicked Call', 'action');
  };

  // Menu View
  const MenuView = () => (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white p-5">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 bg-emerald-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
            M
          </div>
          <div>
            <h3 className="font-semibold text-lg">{COMPANY_NAME}</h3>
            <p className="text-xs text-slate-300 flex items-center gap-1.5">
              <span className="w-2 h-2 bg-emerald-400 rounded-full"></span>
              We typically reply within minutes
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 p-5 bg-slate-50 overflow-y-auto">
        {/* Greeting */}
        <p className="text-slate-700 text-[15px] mb-5">
          Hi there! 👋 How can we help you today?
        </p>

        {/* Action Cards */}
        <div className="space-y-3">
          {/* Live Chat */}
          <button
            onClick={startLiveChat}
            className="w-full flex items-center gap-4 p-4 bg-white rounded-xl border border-slate-200 hover:border-emerald-300 hover:shadow-md transition-all group"
          >
            <div className="w-11 h-11 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
              <MessageCircle className="w-5 h-5 text-white" />
            </div>
            <div className="text-left">
              <h4 className="font-semibold text-slate-800 group-hover:text-emerald-600 transition-colors">Live Chat</h4>
              <p className="text-sm text-slate-500">Chat with our sales team in real-time</p>
            </div>
          </button>

          {/* Book Meeting */}
          <button
            onClick={bookMeeting}
            className="w-full flex items-center gap-4 p-4 bg-white rounded-xl border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all group"
          >
            <div className="w-11 h-11 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
              <Calendar className="w-5 h-5 text-white" />
            </div>
            <div className="text-left">
              <h4 className="font-semibold text-slate-800 group-hover:text-blue-600 transition-colors">Book a Meeting</h4>
              <p className="text-sm text-slate-500">Schedule a consultation call</p>
            </div>
          </button>

          {/* WhatsApp */}
          <button
            onClick={openWhatsApp}
            className="w-full flex items-center gap-4 p-4 bg-white rounded-xl border border-slate-200 hover:border-green-300 hover:shadow-md transition-all group"
          >
            <div className="w-11 h-11 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
            </div>
            <div className="text-left">
              <h4 className="font-semibold text-slate-800 group-hover:text-green-600 transition-colors">Chat on WhatsApp</h4>
              <p className="text-sm text-slate-500">Quick response via WhatsApp</p>
            </div>
          </button>

          {/* Call */}
          <button
            onClick={makeCall}
            className="w-full flex items-center gap-4 p-4 bg-white rounded-xl border border-slate-200 hover:border-purple-300 hover:shadow-md transition-all group"
          >
            <div className="w-11 h-11 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
              <Phone className="w-5 h-5 text-white" />
            </div>
            <div className="text-left">
              <h4 className="font-semibold text-slate-800 group-hover:text-purple-600 transition-colors">Call Us</h4>
              <p className="text-sm text-slate-500">{PHONE_NUMBER}</p>
            </div>
          </button>
        </div>
      </div>

      {/* Footer */}
      <div className="p-3 bg-white border-t border-slate-100 text-center">
        <p className="text-xs text-slate-400">
          Powered by <span className="text-emerald-500 font-medium">{COMPANY_NAME}</span>
        </p>
      </div>
    </div>
  );

  // Live Chat View
  const LiveChatView = () => (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white p-4">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => setCurrentView('menu')}
            className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="w-9 h-9 bg-emerald-500 rounded-full flex items-center justify-center text-white font-bold">
            M
          </div>
          <div className="flex-1">
            <h3 className="font-semibold">{COMPANY_NAME}</h3>
            <p className="text-xs text-slate-300 flex items-center gap-1.5">
              <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></span>
              Online
            </p>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 p-4 bg-slate-50 overflow-y-auto space-y-4">
        {messages.map((msg, idx) => (
          <div key={idx} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
            {msg.type === 'bot' && (
              <div className="flex items-end gap-2 max-w-[85%]">
                <div className="w-7 h-7 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-xs font-bold">M</span>
                </div>
                <div className="bg-white p-3 rounded-2xl rounded-bl-md shadow-sm border border-slate-100">
                  <p className="text-sm text-slate-700">{msg.text}</p>
                </div>
              </div>
            )}
            {msg.type === 'user' && (
              <div className="bg-emerald-500 text-white p-3 rounded-2xl rounded-br-md max-w-[85%]">
                <p className="text-sm">{msg.text}</p>
              </div>
            )}
          </div>
        ))}
        
        {isTyping && (
          <div className="flex items-end gap-2">
            <div className="w-7 h-7 bg-emerald-500 rounded-full flex items-center justify-center">
              <span className="text-white text-xs font-bold">M</span>
            </div>
            <div className="bg-white p-3 rounded-2xl rounded-bl-md shadow-sm">
              <div className="flex gap-1.5">
                <span className="w-2 h-2 bg-slate-300 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></span>
                <span className="w-2 h-2 bg-slate-300 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></span>
                <span className="w-2 h-2 bg-slate-300 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></span>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit} className="p-3 bg-white border-t border-slate-100">
        <div className="flex items-center gap-2">
          <input
            type={chatStep === 'email' ? 'email' : 'text'}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder={
              chatStep === 'name' ? "Enter your name..." :
              chatStep === 'email' ? "Enter your email..." :
              "Type your message..."
            }
            className="flex-1 px-4 py-2.5 bg-slate-100 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:bg-white transition-all"
            autoFocus
          />
          <button
            type="submit"
            className="p-2.5 bg-emerald-500 text-white rounded-full hover:bg-emerald-600 transition-colors"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </form>
    </div>
  );

  return (
    <>
      {/* Floating Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all flex items-center justify-center group"
        >
          <MessageCircle className="w-6 h-6" />
          {/* Pulse effect */}
          <span className="absolute w-full h-full rounded-full bg-emerald-500 animate-ping opacity-30"></span>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 z-50 w-[370px] h-[520px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-slate-200">
          {currentView === 'menu' ? <MenuView /> : <LiveChatView />}
          
          {/* Close Button */}
          <button
            onClick={() => {
              setIsOpen(false);
              setCurrentView('menu');
            }}
            className="absolute bottom-4 right-4 w-10 h-10 bg-slate-800 hover:bg-slate-700 text-white rounded-full shadow-lg flex items-center justify-center transition-colors z-10"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}
    </>
  );
};

export default ChatbotWidget;
